import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo1.png';
import bgImage from '../assets/bg1.jpg';

const styles = {
    screenWrapper: {
        display: "flex",
        flexDirection: "row",
        minHeight: "100vh",
        width: "100%",
        fontFamily: "'Inter', -apple-system, sans-serif",
        backgroundColor: "#fff",
        margin: 0,
        padding: 0,
        overflowX: "hidden",
    },
    leftSection: {
        flex: "1",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
        position: "relative",
    },
    rightSection: {
        flex: "1.2",
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        // Same rounded corners on all 4 sides as requested
        borderRadius: "30px", 
        margin: "20px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    },
    headerStyle: {
        position: 'absolute',
        top: '40px',
        left: '40px',
    },
    logoStyle: {
        height: '45px',
        width: 'auto',
        objectFit: 'contain',
    },
    formBox: {
        width: "100%",
        maxWidth: "380px",
    },
    loginHeading: {
        fontSize: "2.2rem",
        fontWeight: "800",
        color: "#111",
        marginBottom: "8px",
        letterSpacing: "-0.5px",
    },
    subHeading: {
        fontSize: "1rem",
        color: "#666",
        marginBottom: "35px",
    },
    inputGroup: {
        marginBottom: "20px",
        width: "100%",
    },
    label: {
        display: "block",
        fontSize: "0.85rem",
        fontWeight: "600",
        color: "#333",
        marginBottom: "8px",
    },
    input: {
        width: "100%",
        padding: "14px 16px",
        borderRadius: "12px",
        border: "1px solid #E2E8F0",
        fontSize: "1rem",
        backgroundColor: "#F8FAFC",
        outline: "none",
        transition: "all 0.3s ease",
        boxSizing: 'border-box',
    },
    inputFocus: {
        borderColor: "#AE7D54",
        backgroundColor: "#fff",
        boxShadow: "0 0 0 4px rgba(174, 125, 84, 0.1)",
    },
    button: {
        width: "100%",
        padding: "16px",
        backgroundColor: "#AE7D54",
        color: "#fff",
        border: "none",
        borderRadius: "12px",
        fontSize: "1rem",
        fontWeight: "700",
        cursor: "pointer",
        marginTop: "10px",
        transition: "all 0.2s ease",
        boxShadow: "0 10px 15px -3px rgba(174, 125, 84, 0.3)",
    },
    // Modern Success/Error Toast
    toast: {
        position: 'fixed',
        top: '25px',
        right: '25px',
        padding: '16px 24px',
        borderRadius: '16px',
        backgroundColor: '#fff',
        boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        zIndex: 10000,
        fontWeight: '600',
        minWidth: '280px',
        animation: 'slideIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
    }
};

function App() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [focused, setFocused] = useState('');
    const [notification, setNotification] = useState({ show: false, msg: '', type: '' });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 850);

    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 850);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const triggerNotification = (msg, type) => {
        setNotification({ show: true, msg, type });
        setTimeout(() => setNotification({ show: false, msg: '', type: '' }), 4000);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!username || !password) {
            triggerNotification('Please fill in all fields', 'error');
            return;
        }

        try {
            const response = await fetch('https://admin.thepridehotel.in/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });
            const data = await response.json();

            if (response.ok) {
                triggerNotification('Welcome back! Login successful.', 'success');
                localStorage.setItem('isLoggedIn', 'true');
                setTimeout(() => navigate('/dashboard'), 1500);
            } else {
                triggerNotification(data.error || 'Invalid username or password.', 'error');
            }
        } catch (error) {
            triggerNotification('Server error. Please try again later.', 'error');
        }
    };

    return (
        <div style={styles.screenWrapper}>
            {/* Success/Error Notification */}
            {notification.show && (
                <div style={{
                    ...styles.toast,
                    borderLeft: `6px solid ${notification.type === 'success' ? '#10B981' : '#EF4444'}`,
                    // Mobile centering logic
                    left: isMobile ? '20px' : 'auto',
                    right: isMobile ? '20px' : '25px',
                    top: isMobile ? '15px' : '25px',
                }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: notification.type === 'success' ? '#D1FAE5' : '#FEE2E2',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '18px'
                    }}>
                        {notification.type === 'success' ? '✅' : '⚠️'}
                    </div>
                    <div style={{ color: '#1F2937', fontSize: '0.95rem' }}>{notification.msg}</div>
                </div>
            )}

            {/* Left Section - Login Form */}
            <div style={styles.leftSection}>
                <header style={{
                    ...styles.headerStyle,
                    top: isMobile ? '25px' : '40px',
                    left: isMobile ? '25px' : '40px'
                }}>
                    <img src={logo} alt="The Pride Logo" style={styles.logoStyle} />
                </header>

                <div style={{
                    ...styles.formBox,
                    marginTop: isMobile ? '80px' : '0px'
                }}>
                    <h1 style={styles.loginHeading}>Sign in</h1>
                    <p style={styles.subHeading}>Welcome back! Enter your details to continue.</p>

                    <form onSubmit={handleSubmit}>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Username</label>
                            <input
                                type="text"
                                placeholder="Enter your username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                onFocus={() => setFocused('user')}
                                onBlur={() => setFocused('')}
                                style={{
                                    ...styles.input,
                                    ...(focused === 'user' ? styles.inputFocus : {})
                                }}
                            />
                        </div>

                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Password</label>
                            <input
                                type="password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onFocus={() => setFocused('pass')}
                                onBlur={() => setFocused('')}
                                style={{
                                    ...styles.input,
                                    ...(focused === 'pass' ? styles.inputFocus : {})
                                }}
                            />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '25px', fontSize: '0.85rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#666' }}>
                                <input type="checkbox" style={{ marginRight: '8px' }} /> Remember me
                            </label>
                            <span style={{ color: '#AE7D54', fontWeight: '600', cursor: 'pointer' }}>Forgot password?</span>
                        </div>

                        <button 
                            type="submit" 
                            style={styles.button}
                            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                        >
                            Continue
                        </button>
                    </form>

                    <p style={{ textAlign: 'center', marginTop: '30px', color: '#666', fontSize: '0.9rem' }}>
                        Don't have an account? <span style={{ color: '#AE7D54', fontWeight: '700', cursor: 'pointer' }}>Contact Admin</span>
                    </p>
                </div>
            </div>

            {/* Right Section - Image Area (Hidden on Mobile) */}
            {!isMobile && <div style={styles.rightSection}></div>}

            <style>
                {`
                @keyframes slideIn {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                @media (max-width: 850px) {
                    @keyframes slideIn {
                        from { transform: translateY(-50px); opacity: 0; }
                        to { transform: translateY(0); opacity: 1; }
                    }
                }
                `}
            </style>
        </div>
    );
}

export default App;